import {
  useIsTopoff,
  useNextLenderInCascade,
} from "@versatile-credit-apply/sdk"
import { computed, type Ref, toValue, unref } from "vue"
import type { MaybeRef } from "@vueuse/core"
import { type ProviderApplyResponseView } from "@versatile-credit-apply/omni-apply-client"
import { useI18n } from "vue-i18n"

type ProviderEndActionType =
  | "checkout"
  | "done"
  | "next"
  | "topoff"
  | "next-submit"

interface ProviderEndAction {
  type: ProviderEndActionType
  label: string
}

interface UseProviderEndActionOptions {
  response?: MaybeRef<ProviderApplyResponseView>
  type?: ProviderEndActionType
}

export const useProviderEndAction = (
  options?: UseProviderEndActionOptions
): Readonly<Ref<ProviderEndAction>> => {
  const { t } = useI18n({ useScope: "parent" })
  const isTopoff = useIsTopoff()
  const nextLender = useNextLenderInCascade()

  const type = computed<ProviderEndActionType>(() => {
    if (options?.type) {
      return options.type
    }

    // Synchrony has verbiage on their decline screens that makes them consent to MPR
    // upon clicking continue. Upon clicking next-submit, we automatically submit the next
    // lenders
    const response = toValue(options?.response)
    if (
      response?.providerId == "synchrony" &&
      nextLender.value &&
      !isTopoff.value
    ) {
      return "next-submit"
    }

    if (nextLender.value) {
      return isTopoff.value ? "topoff" : "next"
    }

    if (checkout.value) {
      return "checkout"
    }

    return "done"
  })

  const label = computed(() => {
    switch (type.value) {
      case "checkout":
        return t("actions.next")
      case "next":
      case "next-submit":
        return t("actions.continue")
      case "topoff":
        return t("actions.next")
      default:
        return t("actions.done")
    }
  })

  const checkout = computed(() => {
    const response = options?.response
    return response && !!unref(response).checkoutConfig?.token
  })

  return computed(() => {
    return {
      type: type.value,
      label: label.value,
    }
  })
}
